<template>
    <div class="w-full">
        <div>
            <div class="flex flex-col justify-between sm:flex-row px-3">
                <div class="mt-2 flex items-center">
                    <span class="mr-2">
                        {{ $t("common.jsonDateToPosix.inputDateFormat") }}
                    </span>
                    <radio-button
                        :isChecked="'local' === selectedOption"
                        @clickRadio="onInput('local')"
                        class="mr-2"
                        id="local-time"
                        :label="$t('common.jsonDateToPosix.local')"
                    />
                    <radio-button
                        :isChecked="'utc' === selectedOption"
                        @clickRadio="onInput('utc')"
                        class="mr-2"
                        id="utc"
                        :label="$t('common.jsonDateToPosix.utc')"
                    />
                </div>

                <div class="mt-2 flex flex-col justify-end">
                    <span class="mr-2">
                        {{ $t("common.jsonDateToPosix.posixFormatLabel") }}
                    </span>
                    <select-component
                        :valueCurrent="selectedPosixFormat"
                        @change="(val) => (selectedPosixFormat = val)"
                        hidden-field-name
                        :fieldName="
                            $t('common.jsonDateToPosix.posixFormatLabel')
                        "
                        :list="posixFormatOptions"
                        class="w-48"
                    />
                </div>
            </div>

            <div v-if="error" class="my-3">
                <span class="text-red-700 text-lg">{{ error }}</span>
            </div>
            <span
                v-if="reasonForInvalidity"
                class="text-primary-primary dark:text-primary-secondary text-lg my-3 hidden md:block"
            >
                {{ $t("common.invalidJson") }} <br />
                {{ reasonForInvalidity }}
            </span>
        </div>

        <div class="flex flex-wrap justify-between">
            <div class="md:w-1/2 w-full px-3 mt-5">
                <div>
                    <span class="font-semibold text-lg">
                        {{ $t("common.jsonDateToPosix.jsonInput") }}
                    </span>
                </div>
                <code-editor v-model:value="input" />
            </div>

            <div class="w-full md:hidden flex justify-center mt-4 px-3">
                <button
                    :disabled="isProcessing"
                    @click="processFile"
                    class="flex items-center gap-2 border border-primary-primary hover:bg-white bg-primary-primary text-white hover:text-primary-primary font-bold py-2 px-6 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                    <span>
                        {{
                            isProcessing
                                ? $t("common.processing")
                                : $t("common.process")
                        }}
                    </span>
                </button>
            </div>

            <div
                id="date-to-posix-json-result"
                class="md:w-1/2 w-full px-3 mt-5"
            >
                <div class="flex flex-col">
                    <span class="font-semibold text-lg">
                        {{ $t("common.jsonDateToPosix.jsonOutput") }}
                    </span>
                    <span
                        v-if="reasonForInvalidity"
                        class="text-yellow-500 text-lg my-3 md:hidden"
                    >
                        {{ $t("common.invalidJson") }} {{ reasonForInvalidity }}
                    </span>
                </div>
                <code-editor v-model:value="result" read-only />
            </div>
        </div>

        <div class="hidden md:flex flex-col items-center px-3 mt-6 space-y-2">
            <button
                :disabled="isProcessing"
                @click="processFile"
                class="flex items-center gap-2 border border-primary-primary hover:bg-white bg-primary-primary text-white hover:text-primary-primary font-bold py-2 px-6 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
                <span>
                    {{
                        isProcessing
                            ? $t("common.processing")
                            : $t("common.process")
                    }}
                </span>
            </button>
            <span
                v-if="reasonForInvalidity"
                class="text-yellow-500 text-base text-center"
            >
                {{ $t("common.invalidJson") }}: {{ reasonForInvalidity }}
            </span>
        </div>
    </div>
</template>

<script lang="ts">
import type { DatesAdapter, JsonAdapter } from "inversify-utils-kit";
import { computed, defineComponent, inject, reactive, toRefs } from "vue";
import { useI18n } from "vue-i18n";

import CodeEditor from "@/components/utils/CodeEditor.vue";
import RadioButton from "@/components/utils/RadioButton.vue";
import SelectComponent from "@/components/utils/SelectComponent.vue";

export default defineComponent({
    components: { CodeEditor, RadioButton, SelectComponent },
    setup() {
        const datesAdapter = inject("datesAdapter") as DatesAdapter;
        const jsonAdapter = inject("jsonAdapter") as JsonAdapter;
        const { t } = useI18n();

        const state = reactive({
            input: `{
    "event_start": "2024-07-28T10:30:00.000-05:00",
    "event_end": "2024-07-28T12:00:00.000-05:00"
}`,
            result: `{
    "event_start": 1722180600000,
    "event_end": 1722186000000
}`,
            error: "",
            selectedOption: "local",
            reasonForInvalidity: "",
            isProcessing: false,
            selectedPosixFormat: "ns"
        });

        const posixFormatOptions = computed(() => [
            { value: "ms", name: t("common.dateToPosix.milliseconds") },
            { value: "us", name: t("common.dateToPosix.microseconds") },
            { value: "ns", name: t("common.dateToPosix.nanoseconds") }
        ]);

        const goToResultContainer = () => {
            const section = document.getElementById(
                "date-to-posix-json-result"
            );
            if (section) {
                section.scrollIntoView({ behavior: "smooth" });
            }
        };

        const processFile = async () => {
            state.isProcessing = true;
            state.reasonForInvalidity = "";
            state.result = "...";
            const jsonParsed = jsonAdapter.parseJsonStringToString(state.input);
            state.reasonForInvalidity = jsonParsed.reasonForInvalidity;
            if (state.reasonForInvalidity !== "") {
                state.input = jsonParsed.result;
                state.isProcessing = false;
                state.result = "error";
                return;
            }
            await new Promise((resolve) => setTimeout(resolve, 300));

            const result = jsonParsed.result.replace(
                /"(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)"/g,
                (match: string, dateString: string) => {
                    try {
                        const isLocal = state.selectedOption === "local";
                        let posix = datesAdapter.fromFormat(
                            dateString,
                            "yyyy-MM-dd'T'HH:mm:ss.SSSZ",
                            isLocal
                        );
                        if (state.selectedPosixFormat === "us") {
                            return String(posix) + "000";
                        } else if (state.selectedPosixFormat === "ns") {
                            return String(posix) + "000000";
                        } else {
                            return String(posix);
                        }
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    } catch (error) {
                        return match;
                    }
                }
            );
            state.result = result;
            goToResultContainer();
            state.isProcessing = false;
        };

        const onInput = (selected: string) => {
            state.selectedOption = selected;
        };
        return { processFile, onInput, ...toRefs(state), posixFormatOptions };
    }
});
</script>
